// src/pages/Community/CommunityPage.tsx
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BadgeCheck,
  Clapperboard,
  Megaphone,
  Sparkles,
  Users,
} from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import StudioHero from '../../components/Studio/StudioHero';
import StudioSection from '../../components/Studio/StudioSection';
import MemberCard from '../../components/Studio/MemberCard';
import CastingCallCard from '../../components/Studio/CastingCallCard';
import {
  StudioCallout,
  StudioEmptyState,
  StudioErrorState,
  StudioSkeleton,
} from '../../components/Studio/StudioStates';
import {
  CONTAINER,
  PANEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import castingService from '../../services/castingService';
import communityService from '../../services/communityService';

/** What being a member actually gets you. Concrete, not aspirational. */
const MEMBER_PROMISES = [
  {
    icon: BadgeCheck,
    title: 'Your own page, your own URL',
    body: 'A permanent profile at strymhub.com/community/troupe/your-name, with your craft, your reel and every credit you earn here.',
  },
  {
    icon: Clapperboard,
    title: 'Credited properly, every time',
    body: 'Your name sits on the production page next to the work. Not buried, not "and team", not forgotten.',
  },
  {
    icon: Megaphone,
    title: 'Casting calls come to you',
    body: 'When we cast, we look inside the troupe first. Members apply directly — no agent, no gatekeeper, no cold email.',
  },
  {
    icon: Sparkles,
    title: 'A body of work that compounds',
    body: 'One short becomes three. Three becomes a reel. A reel becomes a career you built in public, with receipts.',
  },
];

const CommunityPage = () => {
  useSEO({
    title: 'The StrymHub Community — Join the Troupe',
    description:
      'StrymHub is built around a repertory of actors, writers, directors, crew and collaborators. Join the troupe, get your own profile, and get cast in our productions.',
    keywords:
      'strymhub community, join the troupe, sheffield actors, uk crew, film community, casting community, indie film collective, actor profiles',
    type: 'website',
  });

  const stats = useStudioResource((signal) => communityService.getStats(signal), []);
  const members = useStudioResource(
    (signal) => communityService.getMembers({ size: 8 }, signal),
    [],
  );
  const calls = useStudioResource(
    (signal) => castingService.getOpenCalls({ size: 3 }, signal),
    [],
  );

  const heroStats = stats.data
    ? [
        { label: 'Members', value: String(stats.data.members) },
        { label: 'Crafts represented', value: String(stats.data.crafts.length) },
        { label: 'Productions', value: String(stats.data.productions) },
        { label: 'Open calls', value: String(stats.data.openCalls) },
      ]
    : undefined;

  return (
    <StudioShell>
      <StudioHero
        eyebrow="The StrymHub Community"
        title="A studio is only as good as"
        accent="the people in it."
        lede="StrymHub isn't a place you scroll — it's a repertory you belong to. Actors, writers, directors, camera, sound, design, animation. We cast from inside our own community, so nobody here has to wait to be discovered by somebody else."
        stats={heroStats}
        actions={
          <>
            <Link to="/community/join" className={PRIMARY_BUTTON}>
              Join the troupe
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/community/troupe" className={SECONDARY_BUTTON}>
              Meet the members
            </Link>
          </>
        }
      />

      {/* The pitch to talent */}
      <StudioSection
        eyebrow="Why join"
        title="What membership actually means"
        description="No fees, no subscriptions, no pay-to-audition. You join, we review, and you become part of the pool every StrymHub production casts from."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {MEMBER_PROMISES.map((promise) => (
            <div key={promise.title} className={`${PANEL} p-6`}>
              <promise.icon className="h-6 w-6 text-orange-400" aria-hidden="true" />
              <h3 className="mt-4 font-display text-base font-bold text-white">{promise.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-400">{promise.body}</p>
            </div>
          ))}
        </div>
      </StudioSection>

      {/* Craft breakdown */}
      {stats.data && stats.data.crafts.length > 0 && (
        <section className="border-y border-white/10 bg-white/[0.015] py-16">
          <div className={CONTAINER}>
            <h2 className="heading-cinematic text-2xl text-white sm:text-3xl">
              Who&apos;s already here
            </h2>
            <ul className="mt-8 flex flex-wrap gap-3">
              {stats.data.crafts.map((craft) => (
                <li key={craft.craft}>
                  <Link
                    to={`/community/troupe?craft=${craft.craft}`}
                    className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-neutral-300 transition-colors hover:border-orange-500/40 hover:text-orange-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                  >
                    {craft.label}
                    <span className="text-xs text-neutral-500">{craft.count}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Members */}
      <StudioSection
        eyebrow="The troupe"
        title="Recently joined"
        linkTo="/community/troupe"
        linkLabel="Browse the full troupe"
      >
        {members.loading && <StudioSkeleton count={4} aspect="aspect-[4/5]" />}

        {!members.loading && members.error && (
          <StudioErrorState message={members.error} onRetry={members.reload} />
        )}

        {!members.loading && !members.error && (members.data?.content.length ?? 0) === 0 && (
          <StudioEmptyState
            icon={<Users className="h-8 w-8" />}
            title="The troupe is forming right now"
            description="We're building the founding repertory. Join today and you'll be one of the first names on the wall."
            action={
              <Link to="/community/join" className={PRIMARY_BUTTON}>
                Become a founding member
              </Link>
            }
          />
        )}

        {!members.loading && !members.error && (members.data?.content.length ?? 0) > 0 && (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {members.data?.content.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>
        )}
      </StudioSection>

      {/* Open calls */}
      <StudioSection
        eyebrow="Casting"
        title="Open calls right now"
        description="Roles in front of camera and positions behind it. Members apply in one click."
        linkTo="/casting"
        linkLabel="All open calls"
      >
        {calls.loading && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className={`${PANEL} h-56 animate-pulse`} />
            ))}
          </div>
        )}

        {!calls.loading && calls.error && (
          <StudioErrorState message={calls.error} onRetry={calls.reload} />
        )}

        {!calls.loading && !calls.error && (calls.data?.content.length ?? 0) === 0 && (
          <StudioEmptyState
            icon={<Megaphone className="h-8 w-8" />}
            title="No open calls at this moment"
            description="Calls go out as each production moves into casting. Join the troupe and you'll be in the pool when the next one opens."
            action={
              <Link to="/community/join" className={PRIMARY_BUTTON}>
                Join the troupe
              </Link>
            }
          />
        )}

        {!calls.loading && !calls.error && (calls.data?.content.length ?? 0) > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {calls.data?.content.map((call) => (
              <CastingCallCard key={call.id} call={call} />
            ))}
          </div>
        )}
      </StudioSection>

      <div className={`${CONTAINER} pb-24`}>
        <StudioCallout
          title="Not a performer? We still need you."
          description="Editors, sound designers, composers, illustrators, production designers, photographers, runners. Every production needs a crew, and every crew position is a credit."
          action={
            <Link to="/community/join" className={PRIMARY_BUTTON}>
              Join as crew
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          }
        />
      </div>
    </StudioShell>
  );
};

export default CommunityPage;
